import {
  Box,
  Code,
  ComposedCard,
  Heading,
  HStack,
  Skeleton,
  SkeletonText,
  Text,
} from '@eigtech/ui-shared-dave'
import { LinkButton, useParams } from '@eigtech/ui-shared-router'
import { SanityClient } from '@sanity/client'
import { isNumber } from 'lodash-es'
import { useMemo } from 'react'
import { HiArrowNarrowLeft, HiArrowNarrowRight } from 'react-icons/hi'
import ReactMarkdown from 'react-markdown'
import { useGetDocumentationDoc, useListDocumentation } from './api/sanity/getDocumentation'

export type DocumentationDocProps = {
  documentationDetailRoute: string
  sanityClient: SanityClient
}

export function DocumentationDoc({
  documentationDetailRoute,
  sanityClient,
}: DocumentationDocProps) {
  const { slug } = useParams({ strict: false })

  const { data: doc, isPending } = useGetDocumentationDoc(sanityClient, { slug: slug ?? '' })

  const { data: documentation } = useListDocumentation(sanityClient)

  const docIndex = useMemo(
    () => documentation?.findIndex((basicDoc) => basicDoc.slug === doc?.slug),
    [documentation, doc]
  )

  const previousDoc = isNumber(docIndex) && documentation?.[docIndex - 1]
  const nextDoc = isNumber(docIndex) && documentation?.[docIndex + 1]

  return (
    <ComposedCard
      footer={
        <HStack justifyContent="space-between" w="full">
          {previousDoc ? (
            <LinkButton
              leftIcon={<HiArrowNarrowLeft />}
              params={{ slug: previousDoc.slug } as never}
              to={documentationDetailRoute}
              variant="outline"
            >
              {previousDoc.name}
            </LinkButton>
          ) : (
            <Box />
          )}
          {!!nextDoc && (
            <LinkButton
              params={{ slug: nextDoc.slug } as never}
              rightIcon={<HiArrowNarrowRight />}
              to={documentationDetailRoute}
            >
              {nextDoc.name}
            </LinkButton>
          )}
        </HStack>
      }
      heading={
        <Skeleton isLoaded={!isPending}>
          <Heading as="h2" size="md">
            {isPending ? 'Loading...' : doc?.name}
          </Heading>
        </Skeleton>
      }
    >
      <Box
        sx={{
          '> *:first-child': {
            mt: 0,
          },
          blockquote: {
            borderColor: 'gray.100',
            borderLeftWidth: '0.25em',
            pl: '3',
          },
          pre: {
            bg: 'gray.100',
            maxW: 'full',
            overflow: 'auto',
            p: '2',
            borderRadius: 'md',
          },
          'ol, ul': {
            pl: '5',
          },
          img: {
            mx: 'auto',
            shadow: 'md',
            my: 4,
          },
          'p, pre, ol, ul, blockquote': {
            mt: '4',
            'p, pre, ol, ul, blockquote': {
              mt: '0',
            },
          },
          'h1, h2, h3, h4, h5, h6': {
            mt: 6,
          },
          hr: {
            my: 4,
            h: '2px',
          },
        }}
      >
        {isPending ? (
          <SkeletonText noOfLines={6} skeletonHeight="6" spacing="4" speed={1} />
        ) : (
          <ReactMarkdown
            components={{
              h1: ({ node: __, ...props }) => <Heading as="h1" fontSize="2xl" {...props} />,
              h2: ({ node: __, ...props }) => <Heading as="h2" fontSize="xl" {...props} />,
              h3: ({ node: __, ...props }) => <Heading as="h3" fontSize="lg" {...props} />,
              h4: ({ node: __, ...props }) => <Heading as="h4" fontSize="md" {...props} />,
              h5: ({ node: __, ...props }) => <Heading as="h5" fontSize="sm" {...props} />,
              h6: ({ node: __, ...props }) => <Heading as="h6" fontSize="xs" {...props} />,
              p: ({ node: __, ...props }) => <Text {...props} />,
              code: ({ node: __, ...props }) => <Code {...props} />,
            }}
          >
            {doc?.content ?? ''}
          </ReactMarkdown>
        )}
      </Box>
    </ComposedCard>
  )
}
